import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Who we are · 关于我们",
    p: [
      "Lumière Club is a private, invitation-only members community for young Malaysian connoisseurs. This Privacy Policy explains what information we collect from you and how we use it.",
      "Lumière Club 是一个仅限受邀加入的马来西亚年轻品味人士社群。本《隐私政策》说明我们收集您的哪些资料、以及我们如何使用它们。",
    ],
  },
  {
    h: "2. Information we collect · 我们收集的资料",
    p: [
      "When you apply or use Lumière Club, we collect: your name, email, WhatsApp / phone number, region in Malaysia, occupation, income range, hobbies, referral code, profile photo and bio (optional), as well as any content you post (stories, group posts, broadcasts, direct messages, event RSVPs, comments).",
      "We also automatically log basic technical data such as your IP address, browser type, device model, the timestamps of your actions, and your push-notification subscription tokens — strictly for security, abuse prevention, and delivering notifications.",
      "申请或使用 Lumière Club 时，我们会收集：您的姓名、电邮、WhatsApp / 电话号码、马来西亚地区、职业、收入范围、爱好、推荐码、头像与个人简介（可选）；以及您在平台内发布的内容（动态、群组帖子、广播、私信、活动报名、评论）。",
      "我们也会自动记录基本技术资料：IP 地址、浏览器类型、设备型号、操作时间戳，以及您的推送通知订阅令牌 — 仅用于安全防护、防止滥用和发送通知。",
    ],
  },
  {
    h: "3. How we use your information · 我们如何使用",
    p: [
      "We use your information solely to: (a) review and approve your membership application, (b) operate the social features of the platform (feed, groups, DM, events, notifications), (c) enforce community standards including the 7-day mute for content violations and contact-information sharing rules, (d) communicate with you about your account (approval, password reset, broadcasts).",
      "Stories are visible to all approved members for 24 hours, after which they are automatically deleted from our database. Group posts and DMs are only visible to authorised recipients.",
      "我们仅将您的资料用于：(a) 审核及批准您的会员申请；(b) 运营平台社交功能（动态、群组、私信、活动、通知）；(c) 执行社群规范，包括 7 天禁言机制 和 联系方式分享规则；(d) 就您的账户事项与您沟通（批准、密码重置、广播通知）。",
      "动态内容仅对所有已批准会员可见 24 小时，之后自动从数据库删除。群组帖子和私信只对授权对象可见。",
    ],
  },
  {
    h: "4. Who can see your information · 谁能看到您的资料",
    p: [
      "Other approved members can see your public profile (name, photo, occupation, region, hobbies, bio) and any content you choose to share publicly. Administrators (Concierge) can view all profiles, group activity, and DM logs strictly for moderation purposes. We do NOT sell, rent, or share your data with third-party advertisers.",
      "其他已批准会员可看到您的公开资料（姓名、头像、职业、地区、爱好、简介）以及您主动公开的内容。管理员（Concierge）有权查阅所有个人资料、群组动态及私信记录 — 仅用于社群秩序维护。我们不会出售、出租或与任何第三方广告商分享您的资料。",
    ],
  },
  {
    h: "5. Data storage & security · 资料储存与安全",
    p: [
      "Your data is stored on cloud infrastructure provided by Emergent (object storage for images) and MongoDB Atlas (account & content database). Passwords are hashed with bcrypt and never stored in plaintext. Login sessions use signed JWT tokens. We apply industry-standard encryption in transit (HTTPS / TLS).",
      "您的资料储存于 Emergent 提供的云端储存（图片）与 MongoDB Atlas（账户与内容资料库）。密码使用 bcrypt 加密储存，绝不以明文储存。登入会话采用签名 JWT 令牌。传输过程一律使用业界标准加密（HTTPS / TLS）。",
    ],
  },
  {
    h: "6. Your rights (Malaysia PDPA 2010) · 您的权利（马来西亚 PDPA 2010）",
    p: [
      "Under the Personal Data Protection Act 2010 (Malaysia), you have the right to: access the personal data we hold about you, request corrections, withdraw consent, request deletion of your account and associated content, and lodge a complaint with the Personal Data Protection Commissioner.",
      "依据《2010 年个人资料保护法令》（马来西亚 PDPA），您有权：查阅我们持有的您的个人资料、要求更正、撤回同意、要求删除您的账户及相关内容、向个人资料保护专员投诉。",
      "To exercise any of these rights, contact us at the email below. We will respond within 21 working days.",
      "如需行使上述任何权利，请通过下方电邮联系我们。我们将在 21 个工作天内回复。",
    ],
  },
  {
    h: "7. Children · 未成年",
    p: [
      "Lumière Club is intended for adults aged 21 and above. We do not knowingly collect data from minors. If you believe we have inadvertently collected such data, please contact us immediately and we will delete it.",
      "Lumière Club 仅供 21 岁以上人士使用。我们不会有意收集未成年人的资料。如发现有相关情况，请立刻联系我们，我们会立即删除。",
    ],
  },
  {
    h: "8. Changes to this policy · 政策更新",
    p: [
      "We may update this policy from time to time. Material changes will be announced via in-app broadcast and push notification. The latest version is always available at this page.",
      "我们可能不时更新本政策。重大更动将通过站内广播及推送通知发出。最新版本永远可在本页查阅。",
    ],
  },
  {
    h: "9. Contact · 联系我们",
    p: [
      "For privacy-related questions, data-access / deletion requests, or any concern, please email concierge@lumiere.club. You may also message the Concierge directly inside the app.",
      "如对隐私事项有疑问，或欲行使资料查阅 / 删除等权利，请来信 concierge@lumiere.club。您也可在 App 内直接私信 Concierge。",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen grain bg-[#0a0a0a] text-white/85" data-testid="privacy-page">
      <header className="border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="privacy-home-link">
            <span className="w-8 h-8 rounded-full border border-[#d4af37]/60 flex items-center justify-center">
              <span className="text-[#d4af37] font-serif">L</span>
            </span>
            <span className="font-serif text-xl tracking-wider">
              Lumière <span className="text-white/40">Club</span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#d4af37] flex items-center gap-2"
            data-testid="privacy-back"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="flex items-center gap-3 text-[#d4af37] text-xs uppercase tracking-[0.3em]">
          <ShieldCheck size={14} strokeWidth={1.5} />
          <span>Privacy Policy · 隐私政策</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
          How we keep your moments private.
        </h1>
        <p className="text-white/50 mt-3 text-sm">
          Last updated · 2026-06-01
        </p>

        <article className="mt-12 space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.h} data-testid={`privacy-section-${s.h.split(".")[0]}`}>
              <h2 className="font-serif text-2xl text-[#d4af37]/95">{s.h}</h2>
              <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-white/75">
                {s.p.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </section>
          ))}
        </article>

        <div className="mt-16 border-t border-white/10 pt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs uppercase tracking-[0.2em] text-white/40">
          <span>© Lumière Club · Est. 2026</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-[#d4af37]" data-testid="privacy-footer-home">Home</Link>
            <Link to="/terms" className="hover:text-[#d4af37]" data-testid="privacy-footer-terms">Terms</Link>
            <Link to="/login" className="hover:text-[#d4af37]" data-testid="privacy-footer-login">Sign In</Link>
            <Link to="/apply" className="hover:text-[#d4af37]" data-testid="privacy-footer-apply">Apply</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
